/**
 * The Meta pixel runtime (kitecorp/transferify#375) — React-free, browser-guarded, and the ONLY place this
 * app touches `window.fbq`, injects a Meta script, or names a Meta cookie.
 *
 * The consent contract is Reddit's (`./redditPixel`) and stricter: nothing loads until advertising consent
 * is granted against wording that NAMES Meta ({@link META_SITES}), on a site Meta is enabled for. There is
 * no snippet in the HTML and no `<noscript>` image, so a visitor who has not decided, who refused, or whose
 * grant predates the wording causes zero requests to a Meta host and leaves `window.fbq` undefined.
 *
 * SSG-safe: nothing here runs at module scope, and every export no-ops outside a browser.
 *
 * ── What fbevents.js does, read rather than recalled ──────────────────────────────────────────────
 * From `https://connect.facebook.net/en_US/fbevents.js`, `fbq.version="2.9.415"`, read 2026-10-07:
 *
 * - SPA page views. `setupSPANavigation` wraps `history.pushState`/`replaceState` and listens to
 *   `popstate` to fire an automatic `PageView` on every in-app navigation, guarded by
 *   `if (t.fbq.disablePushState !== true && ...)` and read once when the script installs. Meta's own page
 *   (developers.facebook.com/docs/meta-pixel/implementation/tag_spa) documents the flag. The same setup
 *   also fires an automatic `PageView` on a back-forward-cache restore (`pageshow` with `persisted`).
 * - Automatic events. `fbq('set','autoConfig',false,id)` becomes `optOut(id, "AutomaticSetup")`, the
 *   plugin that sends button clicks and page metadata. Meta documents it "above the init call"
 *   (developers.facebook.com/docs/meta-pixel/advanced/). Automatic ADVANCED MATCHING is a different
 *   opt-in (`AutomaticMatching`), set in Events Manager, and stays on as the owner decided.
 *
 *   Both are ON, by the owner's answer to Q4 ({@link META_AUTOMATIC_EVENTS}): once the script has started
 *   on an allowed page it reports every later in-app navigation, its address included, and the clicks and
 *   page metadata on it, private pages too, until the visitor withdraws.
 * - The navigation guard is what keeps that from carrying a secret. fbevents.js wraps `history.pushState`
 *   and `replaceState` with `signalsFBEventsInjectMethod` — `t[n]=function(){var r=o.apply(this,t);return
 *   a.apply(this,t),r}`, the original first and its own callback after — and listens to `popstate`. The
 *   guard ({@link installNavigationGuard}) is installed once, before the script exists, and stays for the
 *   page's life, as the script's hooks do: Meta's wrapper wraps the guard's and Meta's `popstate` listener
 *   comes after the guard's, so on every navigation the guard reads the new address FIRST. When it carries
 *   a fragment, a query beyond `fbclid` and `utm_*`, a secret path (a share capability, request ids, an
 *   activation token) or a page off the Meta site, the guard revokes ({@link revokeNow}) before the
 *   script's callback builds its page view, and the pixel stays revoked until the next full page load.
 * - Consent. `consent('revoke')` locks the pixel; while locked every later command except `consent` is
 *   pushed back on `fbq.queue` and nothing is sent, and `consent('grant')` replays that queue. The script's
 *   OWN events go the same way: `fire` re-queues a built event while locked, the button text and the
 *   `dl` it read at the time included, and the unlock drains it (`De.onUnlocked(...at())`). So a grant
 *   later in the same page load would send what the script gathered while locked: the clicks and pages
 *   of a visitor who had withdrawn, or of the secret page the guard revoked for. Hence any revoke, a
 *   withdrawal's or the guard's, holds until the next full page load, where the pixel starts afresh.
 * - Before the script has loaded, the commands wait on `fbq.queue` and the script replays them in order,
 *   reading `location.href` as each event leaves, so a queued page view AHEAD of a revoke would leave with
 *   whatever address the visitor is on when the script arrives. Until then this module owns that queue
 *   ({@link revokeNow}).
 * - A second `init` for the same id SETS the user data only while the first init carried none
 *   (`Y(Me[o].userData)`), sends no event, and is otherwise a `DUPLICATE_PIXEL_ID` warning. That is what
 *   {@link identifyMetaVisitor} relies on, and why it attaches one account per page load at most.
 * - `external_id` is HASHED by the pixel (the identity plugin hashes every key but `uid`) after
 *   normalising, while the server sends the raw uuid. Deduplication does not read it — it is event name
 *   plus event id — so this affects match quality only.
 * - Every event carries `dl` = the whole `location.href`, query and fragment included, and `rl` =
 *   `document.referrer`. Hence {@link metaReportableAddress} and the fragment rule in {@link allowedHere}.
 * - Cookies: `_fbp` (its browser id) and `_fbc` (the click identifier it read from `fbclid`), each for
 *   2160 hours (90 days), plus `_fbleid` for `Lead` events, which this browser never sends. In
 *   localStorage it keeps `multiFbc` and persisted queues keyed `<name>^$<suffix>^$<uuid>`.
 */

import {META_RECIPIENT_VERSION} from "consent/consentStore";
import type {ConsentState} from "consent/consentStore";
import {SECRET_PATH_PATTERN} from "attribution/adAttribution";
import {brandSiteLocaleForHostname} from "hostnamePolicy";
import {localePrefixOf, treeRelativePath} from "seo/documentLang";
import {adPixelRouteAllowed} from "./adPixels";
import type {AdPurchaseKind} from "./AdsApi";
import {expireCookiesMatching} from "./cookieExpiry";
import {clearSignUpPending, signUpPending} from "./signUpMark";

/**
 * The sites Meta runs on, each mapped to the FIRST consent wording version that names Meta for it
 * (kitecorp/transferify#375, owner decision: Romania first).
 *
 * A grant stamped below a site's version was given against wording that did not name Meta, so it keeps
 * Google and Reddit and never reaches Meta, and nobody is asked again. The site is the deployed brand
 * storefront the visitor is on (`hostnamePolicy.brandSiteLocaleForHostname`): its own host, the
 * production `www.` alias and the dev host, never a claimed tenant subdomain (a customer's page, whose
 * visitors are not our audience), a custom domain or loopback.
 *
 * Adding English or German is adding their key here with the version whose wording names Meta for them:
 * the current one if the wording names Meta in general words (open question Q1, option a, which the
 * copy at version 7 does), or a later one if it names Meta "on transferify.ro" only (Q1 b).
 */
export const META_SITES = {ro: META_RECIPIENT_VERSION} as const;

/** A site Meta is enabled for. */
export type MetaSite = keyof typeof META_SITES;

/**
 * The Meta-enabled language trees that ALSO count on another brand storefront, keyed by that storefront's
 * locale (owner answer to Q9: the Romanian pages under `/ro` on the main domain, transferify.eu, its
 * `www.` alias and its dev host). Only the prefixed tree counts there; the main domain's English and German
 * trees, and every other storefront's trees, stay off. The consent dialog under `/ro` is read in Romanian,
 * so the wording that names Meta is the one the visitor saw.
 */
export const META_PREFIXED_TREES: Readonly<Record<string, readonly MetaSite[]>> = {en: ["ro"]};

/**
 * Whether Meta's automatic events run (owner answer to Q4: yes, Meta's defaults). On: no
 * `fbq('set','autoConfig',false,id)`, so the script sends button clicks and page metadata from the pages it
 * reaches, and no `fbq.disablePushState`, so it reports every in-app navigation itself; this module then
 * sends no route page views of its own, or each navigation would be counted twice. Off restores both
 * opt-outs and this module's own page views for allowed pages only.
 */
export const META_AUTOMATIC_EVENTS = true;

const PIXEL_SCRIPT_SOURCE = "https://connect.facebook.net/en_US/fbevents.js";

/**
 * The query parameters an address may carry for the pixel to run on it: Meta's own click identifier and
 * the campaign labels our ad links author. Meta reports the whole address, query included (see the
 * header), so any OTHER parameter — a token on the unsubscribe page, a referral code, a booking id,
 * another network's click identifier — would reach Meta verbatim. Refusing what is not known to be
 * harmless is what keeps this safe for a parameter nobody has added yet; the cost of a forgotten one is a
 * page view not counted, never a value leaked.
 */
const REPORTABLE_QUERY_PARAMETER = /^(fbclid|utm_[a-z]+)$/;

/** The site this host (and, for {@link META_PREFIXED_TREES}, this path) belongs to, or null for none. */
export function metaSiteOf(hostname: string, pathname = "/",
	prefixedTrees: Readonly<Record<string, readonly MetaSite[]>> = META_PREFIXED_TREES): MetaSite | null {
	const storefront = brandSiteLocaleForHostname(hostname);
	if (storefront === null) {
		return null;
	}
	if (isMetaSite(storefront)) {
		return storefront;
	}
	const tree = localePrefixOf(pathname);
	return tree !== null && isMetaSite(tree) && (prefixedTrees[storefront] ?? []).includes(tree) ? tree : null;
}

function isMetaSite(locale: string): locale is MetaSite {
	return Object.prototype.hasOwnProperty.call(META_SITES, locale);
}

/**
 * Whether Meta may run for this visitor on this page: an id is configured, advertising is granted, the
 * stored decision was given against wording that names Meta for this site, and the route is one an ad
 * pixel may run on at all (`adPixels.adPixelRouteAllowed`).
 *
 * ONE answer for the pixel and for keeping Meta's click identifier (`attribution/adAttribution`), so the
 * server never holds an `fbc` for a visitor the pixel would not have run for: the `fbc` is the server's
 * evidence that a grant naming Meta was given on a Meta-enabled site.
 */
export function metaPixelAllowed(input: {
	pixelId: string | null;
	hostname: string;
	pathname: string;
	consent: ConsentState;
	storedVersion: number;
}): boolean {
	const site = metaSiteOf(input.hostname, input.pathname);
	return input.pixelId !== null && input.consent.marketing && site !== null
		&& input.storedVersion >= META_SITES[site] && adPixelRouteAllowed(input.pathname);
}

/**
 * Whether Meta still covers this visitor at all, wherever they are on the site: a dataset id is configured
 * and the stored decision was given against wording that names Meta. The attribution capture drops a
 * stored `fbc` when this is false (a rollback that empties the id, or a decision read below that wording),
 * so the server never receives one for a visitor Meta no longer covers. Deliberately not about the route:
 * a stored `fbc` must not depend on which page the visitor signs in from.
 */
export function metaClickIdKeptFor(input: {pixelId: string | null; storedVersion: number}): boolean {
	return input.pixelId !== null && input.storedVersion >= META_RECIPIENT_VERSION;
}

/** Whether an address's query holds only what may reach Meta (see {@link REPORTABLE_QUERY_PARAMETER}). */
export function metaReportableAddress(search: string): boolean {
	return [...new URLSearchParams(search).keys()].every((name) => REPORTABLE_QUERY_PARAMETER.test(name));
}

type FbqFunction = ((...args: unknown[]) => void) & {
	callMethod?: (...args: unknown[]) => void;
	queue?: unknown[][];
	push?: unknown;
	loaded?: boolean;
	version?: string;
	disablePushState?: boolean;
};

interface PixelWindow extends Window {
	fbq?: FbqFunction;
	_fbq?: FbqFunction;
}

/** The pixel id currently running, or null while nothing is running. Also the single-flight latch. */
let running: string | null = null;

/**
 * Whether the pixel was revoked in this page load, by a withdrawal or by the navigation guard. Never reset:
 * a grant would hand the script back what it gathered while locked (see the header), so the next start is
 * the next full page load's.
 */
let revokedThisPageLoad = false;

/** Whether the navigation guard is installed; it then stays for the page's life (see the header). */
let guardInstalled = false;

/** Whether an account's id has reached the pixel in this page load (see {@link identifyMetaVisitor}). */
let identified = false;

/** The path a view has already been counted for, as `redditPixel` keeps it and for the same reason. */
let viewedPath: string | null = null;

/** Whether the pixel is running, so the orchestrator asks rather than keeping a second latch in step. */
export function metaPixelRunning(): boolean {
	return running !== null;
}

/** Whether an event sent now would reach the pixel: it is running and this page passes {@link allowedHere}. */
export function metaPixelReportsHere(): boolean {
	const scope = pixelWindow();
	return scope !== null && running !== null && allowedHere(scope);
}

function pixelWindow(): PixelWindow | null {
	return typeof window === "undefined" ? null : (window as PixelWindow);
}

/**
 * The page rule, applied where the pixel is TOUCHED rather than where a caller decides to: a Meta site,
 * a route an ad pixel may run on, an address whose query Meta may receive, and no fragment. Read off
 * `window.location`, because that is what the script itself reports, and it reports `location.href`
 * whole, so a fragment (`#k=` share keys, `#finish=` and `#stop=` tokens) would reach Meta as well.
 *
 * {@link stopMetaPixel} is deliberately NOT gated: a withdrawal must take effect on whatever page the
 * visitor withdrew from.
 */
function allowedHere(scope: PixelWindow): boolean {
	const {hostname, pathname, search, hash} = scope.location;
	return metaSiteOf(hostname, pathname) !== null && adPixelRouteAllowed(pathname) && metaReportableAddress(search)
		&& hash === "";
}

/**
 * Whether the address the visitor has navigated to may reach Meta through the script's own reports: on a
 * Meta site, no fragment, a query of `fbclid` and `utm_*` only, and not a path that is itself a secret (a
 * recipient page names the transfer or the request; an activation page carries its token). The route
 * scope's other private pages are NOT refused here: the owner chose Meta's automatic events inside the
 * account too (Q4), and their paths carry nothing secret.
 */
function navigationReportable(scope: PixelWindow): boolean {
	const {hostname, pathname, search, hash} = scope.location;
	return metaSiteOf(hostname, pathname) !== null && hash === "" && metaReportableAddress(search)
		&& !SECRET_PATH_PATTERN.test(treeRelativePath(pathname));
}

/**
 * One navigation as the guard answers it: nothing while the pixel is not running, and a revoke for the rest
 * of the page load when the new address is one Meta must not receive.
 */
function guardNavigation(scope: PixelWindow): void {
	if (running !== null && !navigationReportable(scope)) {
		revokeForThisPageLoad(scope);
	}
}

/**
 * Hook every way the address changes in place — `history.pushState`, `history.replaceState`, `popstate`
 * and `hashchange` — with ONE check, BEFORE Meta's script exists, so it runs ahead of the script's own
 * history wrapper and `popstate` listener (see the header). Never removed: the script's hooks stay for the
 * page's life, and a guard installed again later would wrap THEM and listen after them, reading each new
 * address only once the script had. {@link guardNavigation} is what makes it inert while nothing runs.
 */
function installNavigationGuard(scope: PixelWindow): void {
	if (guardInstalled) {
		return;
	}
	guardInstalled = true;
	const history = scope.history;
	const originalPush = history.pushState;
	const originalReplace = history.replaceState;
	const check = () => guardNavigation(scope);
	history.pushState = function (this: History, ...args: Parameters<History["pushState"]>) {
		const result = originalPush.apply(this, args);
		check();
		return result;
	};
	history.replaceState = function (this: History, ...args: Parameters<History["replaceState"]>) {
		const result = originalReplace.apply(this, args);
		check();
		return result;
	};
	scope.addEventListener("popstate", check);
	scope.addEventListener("hashchange", check);
}

/**
 * Lock the pixel at once. Once fbevents.js runs, that is `consent('revoke')`. Before it has loaded, the
 * queue is still this module's, and the script would replay it in order, reading the address as each event
 * leaves (see the header): so the revoke goes FIRST, every queued event is dropped, and so is every earlier
 * consent command, so nothing after the revoke unlocks it. `init` and `set` stay: they send nothing.
 */
function revokeNow(scope: PixelWindow): void {
	const fbq = fbqOf(scope);
	if (fbq.callMethod) {
		fbq("consent", "revoke");
		return;
	}
	const queue = fbq.queue!;
	const silent = queue.filter(([command]) => command !== "consent" && !String(command).startsWith("track"));
	queue.splice(0, queue.length, ["consent", "revoke"], ...silent);
}

/** Revoke, and hold that until the next full page load (see {@link revokedThisPageLoad}). */
function revokeForThisPageLoad(scope: PixelWindow): void {
	revokeNow(scope);
	revokedThisPageLoad = true;
	running = null;
	viewedPath = null;
}

/**
 * The `fbq` stub, installed exactly as Meta's base code documents it: commands issued before fbevents.js
 * arrives wait on `fbq.queue` and are replayed by the script. With {@link META_AUTOMATIC_EVENTS} off,
 * `disablePushState` is set here, on the stub, because the script reads it once when it installs.
 */
function fbqOf(scope: PixelWindow): FbqFunction {
	if (!scope.fbq) {
		const stub: FbqFunction = function fbq(...args: unknown[]) {
			if (stub.callMethod) {
				stub.callMethod(...args);
			} else {
				stub.queue!.push(args);
			}
		} as FbqFunction;
		if (!scope._fbq) {
			scope._fbq = stub;
		}
		stub.push = stub;
		stub.loaded = true;
		stub.version = "2.0";
		stub.queue = [];
		if (!META_AUTOMATIC_EVENTS) {
			stub.disablePushState = true;
		}
		scope.fbq = stub;
	}
	return scope.fbq;
}

/**
 * Every command handed to `fbq` so far, as plain arrays, so "nothing was sent before consent" is a fact a
 * test can assert. Reflects the queue while fbevents.js is still loading, which is the window it polices.
 */
export function metaPixelCommands(): unknown[][] {
	return pixelWindow()?.fbq?.queue?.map((entry) => Array.from(entry)) ?? [];
}

/**
 * Load and initialise the pixel iff {@link metaPixelAllowed} holds and this address may reach Meta.
 *
 * Single-flight: repeated calls never re-init and never inject a second script. Refused for the rest of
 * the page load once the pixel was revoked in it, by a withdrawal or by the navigation guard: a grant would
 * hand the script back what it gathered while locked (see the header). The next full page load starts it.
 *
 * @param input.externalId the signed-in account's own uuid (api#1240), RAW, or absent for a signed-out
 *                         visitor. The only value passed by hand: Automatic Advanced Matching (Email,
 *                         Country, External id, Gender) is Meta's own setting in Events Manager.
 * @returns whether the pixel is running after this call
 */
export function startMetaPixel(input: {
	pixelId: string | null;
	consent: ConsentState;
	storedVersion: number;
	externalId?: string | null;
}): boolean {
	const scope = pixelWindow();
	if (scope === null) {
		return false;
	}
	if (running !== null) {
		return true;
	}
	const {pixelId, consent, storedVersion} = input;
	const {hostname, pathname} = scope.location;
	if (revokedThisPageLoad || pixelId === null
		|| !metaPixelAllowed({pixelId, hostname, pathname, consent, storedVersion}) || !allowedHere(scope)) {
		return false;
	}
	// Before the base code, so the guard is ahead of the script's own history hooks (see the header).
	installNavigationGuard(scope);
	const fbq = fbqOf(scope);
	const externalId = input.externalId ?? "";
	if (!META_AUTOMATIC_EVENTS) {
		fbq("set", "autoConfig", false, pixelId);
	}
	fbq("init", pixelId, externalId === "" ? {} : {external_id: externalId});
	identified = externalId !== "";
	fbq("track", "PageView");
	if (scope.document.querySelector("script[data-meta-pixel]") === null) {
		const script = scope.document.createElement("script");
		script.async = true;
		script.src = PIXEL_SCRIPT_SOURCE;
		script.setAttribute("data-meta-pixel", "");
		scope.document.head.appendChild(script);
	}
	running = pixelId;
	viewedPath = pathname;
	return true;
}

/**
 * Attach the account to a pixel that started signed out (a sign-in mid-visit): a second `init` with
 * `external_id`, which the script accepts while the first carried no user data and which sends no event.
 *
 * At most once per page load, because the script ignores any later one (see the header); the account
 * then reaches Meta from the next full load. That costs match quality only: deduplication reads the
 * event id, which the sign-up and purchase events carry whatever this attached.
 *
 * @returns whether the id reached the pixel
 */
export function identifyMetaVisitor(externalId: string | null | undefined): boolean {
	const scope = pixelWindow();
	if (scope === null || running === null || identified || !externalId || !allowedHere(scope)) {
		return false;
	}
	fbqOf(scope)("init", running, {external_id: externalId});
	identified = true;
	return true;
}

/**
 * A route change, counted once per path, exactly as `redditPixel.trackRedditPageVisit` counts it — only
 * while {@link META_AUTOMATIC_EVENTS} is off. On, the script reports every in-app navigation itself
 * (see the header), and a page view from here as well would count each one twice.
 */
export function trackMetaPageView(): void {
	const scope = pixelWindow();
	if (META_AUTOMATIC_EVENTS || scope === null || scope.location.pathname === viewedPath) {
		return;
	}
	if (send("PageView")) {
		viewedPath = scope.location.pathname;
	}
}

/** One purchase, as {@link trackMetaPurchase} reports it. */
export interface MetaPurchase {
	/** The id the SERVER reported this purchase under, verbatim: Meta deduplicates on event name plus id. */
	conversionId: string;
	/** Which purchase it is, as the api says, so the browser picks the event name the server sends. */
	kind: AdPurchaseKind;
	/** What was paid, in MAJOR units of {@link currency}. */
	value: number;
	/** ISO 4217, upper case, the spelling the server's own report uses. */
	currency: string;
}

/**
 * The browser twin of each server event that has one (design table 2.2), as Meta names it, with the
 * event id that makes Meta count the two as one: event name plus `eventID` equal to the server's
 * `event_id`.
 *
 * Three server events have NO browser twin and so no entry here: the first transfer, a plan limit and a
 * checkout start happen where the pixel does not run or before anything is known in this browser, and the
 * server reports them alone under `first-transfer:<account>`, `limit-reached:<...>` and
 * `checkout-started:<session id>`.
 */
export const META_BROWSER_EVENTS = {
	signUp: "CompleteRegistration",
	SUBSCRIPTION: "Subscribe",
	ONE_OFF: "Purchase",
} as const;

/**
 * A completed purchase (kitecorp/transferify-api-service#1241): `Subscribe` for a subscription invoice,
 * `Purchase` for a one-off, with `value` and `currency` and the server's conversion id as the event id.
 *
 * @returns whether the event actually left, so the caller remembers only what was sent
 */
export function trackMetaPurchase({conversionId, kind, value, currency}: MetaPurchase): boolean {
	return send(META_BROWSER_EVENTS[kind], {value, currency}, {eventID: conversionId});
}

/**
 * Spend an owed sign-up on this page, if one is owed to Meta and this page may carry it, as
 * `CompleteRegistration` with the account's raw uuid as its event id: the SAME string the server sends as
 * `event_id` for the account-established fact, so the two are one registration in Meta.
 *
 * The mark is spent only when the event left, for the reason `redditPixel.trackRedditSignUp` gives.
 */
export function trackMetaSignUp(accountId: string | null | undefined): void {
	if (!accountId || !signUpPending("meta")) {
		return;
	}
	if (send(META_BROWSER_EVENTS.signUp, {}, {eventID: accountId})) {
		clearSignUpPending("meta");
	}
}

/**
 * The cookie names {@link stopMetaPixel} deletes, as a PREFIX rule: `_fbp`, `_fbc`, `_fbleid` and any
 * later one. Exported because the Cookie Policy prints `_fbp` and `_fbc` as published copy and
 * `src/i18n/cookiePolicyIdentifiers.test.ts` holds them to this pattern.
 */
export const META_COOKIE_NAME_PATTERN = /^_fb/;

/** Meta's web-storage entries (see the header): `multiFbc` and its persisted queues (`...^$...`). */
const META_STORAGE_KEY_PATTERN = /^multiFbc$|\^\$/;

/**
 * Withdrawal: stop sending NOW. The running pixel is revoked until the next full page load
 * ({@link revokeForThisPageLoad}), the latch is dropped so every export above no-ops, and the identifiers
 * Meta wrote are expired, whether or not the pixel ran in THIS page load, for the reason
 * `redditPixel.stopRedditPixel` gives.
 */
export function stopMetaPixel(): void {
	const scope = pixelWindow();
	if (scope === null) {
		return;
	}
	if (running !== null) {
		revokeForThisPageLoad(scope);
	}
	expireCookiesMatching(scope.document, META_COOKIE_NAME_PATTERN);
	clearMetaStorage(scope);
}

function clearMetaStorage(scope: PixelWindow): void {
	try {
		const storage = scope.localStorage;
		const keys = Array.from({length: storage.length}, (_, index) => storage.key(index) ?? "");
		keys.filter((key) => META_STORAGE_KEY_PATTERN.test(key)).forEach((key) => storage.removeItem(key));
	} catch {
		// A storage area that refuses access holds nothing Meta could have written either.
	}
}

/**
 * Meta's browser id as its pixel wrote it (`_fbp`), or null when there is none — sent to our api beside
 * the click identifier so the server's events carry it too (`attribution/adAttribution.metaBrowserIdFor`).
 * Read at submission rather than at capture, because the script writes it only once it has loaded.
 */
export function metaBrowserIdCookie(): string | null {
	const scope = pixelWindow();
	if (scope === null) {
		return null;
	}
	const entry = scope.document.cookie.split(";").map((part) => part.trim()).find((part) => part.startsWith("_fbp="));
	const value = entry?.slice("_fbp=".length) ?? "";
	return value === "" ? null : value;
}

/** @returns whether the command reached `fbq`, which a caller must not assume. */
function send(event: string, ...params: Record<string, unknown>[]): boolean {
	const scope = pixelWindow();
	if (scope === null || running === null || !allowedHere(scope)) {
		return false;
	}
	fbqOf(scope)("track", event, ...params);
	return true;
}
